import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Award, MapPin, Leaf, TreePine, Car, Share2, Copy, Check, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";

export default function Certificate() {
  const { certId } = useParams();
  const [cert, setCert] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/certificates/${certId}`)
      .then((r) => setCert(r.data))
      .catch(() => setError(true));
  }, [certId]);

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: "Carbon Vaults certificate", url }); return; } catch {}
    }
    navigator.clipboard.writeText(url);
    setCopied(true);
    toast.success("Link copied");
    setTimeout(() => setCopied(false), 2500);
  };

  if (error) return (
    <div className="max-w-lg mx-auto px-6 py-24 text-center">
      <h1 className="text-3xl tracking-tighter font-light">Certificate not found</h1>
      <p className="text-stone-600 mt-2">This certificate ID doesn't exist or has been revoked.</p>
      <Link to="/marketplace"><Button className="mt-6 rounded-full bg-[#2C5530] hover:bg-[#1F3D22]">Browse projects</Button></Link>
    </div>
  );
  if (!cert) return <div className="p-12 text-stone-500">Loading certificate…</div>;

  return (
    <div className="max-w-4xl mx-auto px-6 py-10">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-white border border-stone-200 rounded-3xl overflow-hidden shadow-sm"
      >
        {/* Header ribbon */}
        <div className="relative bg-[#2C5530] text-white p-8 sm:p-12">
          <div className="absolute inset-0 opacity-10 mix-blend-overlay">
            <div className="noise-overlay"></div>
          </div>
          <div className="flex items-center justify-between relative">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 bg-white/15 rounded-xl flex items-center justify-center">
                <Leaf className="w-5 h-5"/>
              </div>
              <span className="font-medium tracking-tight text-lg">Carbon Vaults</span>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-[0.2em] text-white/70">Certificate</div>
              <div className="font-mono text-sm mt-1" data-testid="cert-id">{cert.certificate_id}</div>
            </div>
          </div>
          <div className="mt-10 relative">
            <div className="text-xs uppercase tracking-[0.2em] text-white/70">This certifies that</div>
            <h1 className="mt-3 text-4xl sm:text-5xl tracking-tighter font-light" data-testid="cert-company">
              {cert.company_name}
            </h1>
            <p className="mt-3 text-white/80 text-lg">
              has permanently retired <span className="font-medium text-white">{cert.tons.toLocaleString()} metric tons</span> of CO₂ equivalent through Carbon Vaults.
            </p>
            {cert.anonymous && (
              <div className="mt-4 inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-white/60">
                <span className="w-1.5 h-1.5 bg-white/40 rounded-full"/> Retired anonymously
              </div>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="p-8 sm:p-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {cert.project && (
              <div>
                <div className="text-xs uppercase tracking-[0.2em] font-semibold text-stone-500">Project</div>
                <div className="mt-4 flex gap-4">
                  <img src={cert.project.image_url} alt="" className="w-24 h-24 rounded-2xl object-cover"/>
                  <div>
                    <div className="font-medium text-lg leading-tight">{cert.project.name}</div>
                    <div className="text-sm text-stone-500 flex items-center gap-1 mt-1"><MapPin className="w-3.5 h-3.5"/> {cert.project.location}</div>
                    <div className="text-sm text-stone-500 flex items-center gap-1 mt-1"><Award className="w-3.5 h-3.5"/> {cert.project.certification}</div>
                  </div>
                </div>
              </div>
            )}
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-stone-500">Retired on</div>
              <div className="mt-4 text-2xl font-light tracking-tight flex items-center gap-2">
                <CalendarDays className="w-5 h-5 text-[#2C5530]"/>
                {new Date(cert.issued_at).toLocaleDateString(undefined, { year:"numeric", month:"long", day:"numeric" })}
              </div>
              {cert.company_country && <div className="text-sm text-stone-500 mt-2">Retired from {cert.company_country}</div>}
            </div>
          </div>

          {/* Impact stats */}
          <div className="grid grid-cols-3 gap-4 mt-10 pt-10 border-t border-stone-200">
            <div>
              <Leaf className="w-6 h-6 text-[#2C5530]"/>
              <div className="mt-3 text-3xl font-light tracking-tight">{cert.tons.toLocaleString()}</div>
              <div className="text-xs uppercase tracking-widest text-stone-500 mt-1">Tons CO₂</div>
            </div>
            <div>
              <TreePine className="w-6 h-6 text-[#2C5530]"/>
              <div className="mt-3 text-3xl font-light tracking-tight">{cert.trees_equivalent.toLocaleString()}</div>
              <div className="text-xs uppercase tracking-widest text-stone-500 mt-1">Trees / yr</div>
            </div>
            <div>
              <Car className="w-6 h-6 text-[#D97757]"/>
              <div className="mt-3 text-3xl font-light tracking-tight">{cert.cars_off_road.toLocaleString()}</div>
              <div className="text-xs uppercase tracking-widest text-stone-500 mt-1">Cars off road / yr</div>
            </div>
          </div>

          <div className="mt-10 flex flex-wrap items-center justify-between gap-4 pt-8 border-t border-stone-200">
            <div className="text-xs text-stone-500 max-w-md">
              This certificate is publicly verifiable at carbon-vaults.com/certificate/{cert.certificate_id}. Retired credits are permanently removed from circulation.
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={share} data-testid="cert-share" className="rounded-full">
                {copied ? <><Check className="w-4 h-4 mr-1"/>Copied</> : <><Share2 className="w-4 h-4 mr-1"/>Share</>}
              </Button>
              <Button onClick={() => window.print()} className="rounded-full bg-[#2C5530] hover:bg-[#1F3D22] text-white">
                <Copy className="w-4 h-4 mr-1"/> Save PDF
              </Button>
            </div>
          </div>
        </div>
      </motion.div>

      <div className="mt-8 text-center">
        <Link to="/marketplace">
          <Button variant="ghost" className="text-[#2C5530]">Retire your own credits →</Button>
        </Link>
      </div>
    </div>
  );
}
